import { useMemo } from 'react'

import { AnalyticsSource } from '@b-stock/fe-core/bstock-react'

import { useAnalytics } from '@/helpers/telemetry/SegmentAnalytics'
import { useAnalyticsEntityType } from '@/helpers/telemetry/useAnalyticsEntityType'

import type { MediaType } from './types'

export type OpenTrigger =
  | 'fullscreen_button'
  | 'inline_navigate'
  | 'main_image'
  | 'more_tile'
  | 'thumbnail'

export type GalleryView = 'fullscreen' | 'inline'

/** `step` moves one photo (arrow, swipe, key); `jump` picks one (thumbnail, dot, grid). */
export type NavMethod = 'jump' | 'step'

/** Gallery events shared by both LDP galleries, so their counts are comparable. */
export const useGalleryTracking = (media: MediaType[]) => {
  const { trackButtonClicked } = useAnalytics()
  const entityType = useAnalyticsEntityType()

  return useMemo(() => {
    const analyticsData = {
      entityType,
      screenName: 'auction_detail',
      source: AnalyticsSource.HOME_PORTAL,
    }

    const mediaData = (index: number) => ({
      ...analyticsData,
      media_index: index + 1,
      media_type: media[index]?.type === 'video' ? 'video' : 'picture',
      number_of_media_count: media.length,
    })

    return {
      analyticsData,
      trackNavigate: (
        index: number,
        galleryView: GalleryView,
        navMethod: NavMethod
      ) =>
        trackButtonClicked({
          ...mediaData(index),
          buttonName: 'photo_navigate',
          gallery_view: galleryView,
          nav_method: navMethod,
        }),
      trackOpen: (index: number, openTrigger: OpenTrigger) =>
        trackButtonClicked({
          ...mediaData(index),
          buttonName: 'see_all_photos',
          open_trigger: openTrigger,
        }),
      trackSignInPrompt: (index: number, openTrigger: OpenTrigger) =>
        trackButtonClicked({
          ...mediaData(index),
          buttonName: 'photo_sign_in_prompt',
          open_trigger: openTrigger,
        }),
    }
  }, [entityType, media, trackButtonClicked])
}
